<template>
    <UiModalMobile
        :is-open="showAddUsageModal"
        title="Add Benefit Usage"
        @close="closeModal"
    >
        <template #header>
            <div class="flex flex-col w-full">
                <div
                    class="flex items-center justify-between bg-white dark:bg-slate-800 z-10 rounded-t-2xl w-full"
                >
                    <h2 class="text-xl font-medium text-slate-800 dark:text-slate-100">
                        Add Usage
                    </h2>

                    <button @click="closeModal" class="btn-base btn-icon !m-0">
                        <Icon icon="x" class="text-slate-500 dark:text-slate-400" />
                    </button>
                </div>
                <div class="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                    <span>
                        {{ selectedBenefit?.benefit?.benefit_title }} •
                        {{ selectedBenefit?.card?.title }}
                        <span class="text-xs text-slate-500 dark:text-slate-400">
                            ({{ selectedBenefit?.connected_account_last_four }})
                        </span>
                    </span>
                </div>
            </div>
        </template>

        <template #content>
            <div class="w-full">
                <div v-if="!selectedBenefit?.benefit" class="text-center py-8">
                    <Icon
                        icon="alert-circle"
                        class="w-12 h-12 text-slate-400 mx-auto mb-4"
                    />
                    <p class="text-slate-500 dark:text-slate-400">
                        Please select a benefit to add usage.
                    </p>
                </div>

                <BenefitsUsageForm
                    v-else
                    v-model="model"
                    :amountRemainingMessage="
                        isByCountValueBenefit
                            ? `${remainingCount} remaining`
                            : `$${remainingAmount} remaining`
                    "
                    :errors="formErrors"
                    :isCountBased="isCountBased"
                    :countLabel="countLabel"
                    :countRemainingMessage="`${remainingCount} remaining`"
                    :maxAmount="maxFillableAmount"
                    :maxCount="remainingCount"
                />
            </div>
        </template>

        <template #footer>
            <div class="flex gap-3">
                <button
                    type="button"
                    class="flex-1 btn-base btn-white"
                    @click="closeModal"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    class="flex-1 btn-base btn-primary"
                    @click="handleAdd"
                >
                    <span>Add Usage</span>
                </button>
            </div>
        </template>
    </UiModalMobile>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import UiModalMobile from '@/components/ui/modal/ui-modal-mobile.vue';
import Icon from '@/components/ui/icon/icon.vue';
import BenefitsUsageForm from '@/components/card-benefits/benefits-usage-form.vue';
import { useCardBenefitsStore } from '@/store/card-benefits';
import dayjs from 'dayjs';

const store = useCardBenefitsStore();

const props = defineProps({
    showAddUsageModal: {
        type: Boolean,
        default: false,
    },
    selectedBenefit: {
        type: Object,
        default: () => ({}),
    },
});

const emit = defineEmits(['close', 'add']);

const model = ref({
    amount: null,
    count: null,
    date: dayjs().format('YYYY-MM-DD'), // Default to today in user's timezone
});

const formErrors = ref({});

const isCountBased = computed(() => {
    const benefit = props.selectedBenefit?.benefit;
    return store.checkIsCountBased(benefit);
});

const isNcred = computed(
    () => props.selectedBenefit?.benefit?.values_summary?.currency === 'NCRED'
);

const countLabel = computed(() => {
    return props.selectedBenefit?.benefit?.value_details?.count_label || 'uses';
});

const maxCount = computed(() => {
    return (
        props.selectedBenefit?.benefit?.value_details?.count ||
        (isNcred.value ? displayAmountForSelectedDate.value : 0) ||
        0
    );
});

// By-count benefits that still carry a dollar value (e.g. 2 × $250): the user
// enters a $ amount (isCountBased is false), but the meaningful "remaining"
// unit is the number of uses, capped at value_details.count by the backend
// ValidateBenefitUsageCount rule.
const isByCountValueBenefit = computed(() => {
    if (isCountBased.value) {
        return false;
    }
    const count =
        parseFloat(props.selectedBenefit?.benefit?.value_details?.count) || 1;
    return count > 1;
});

// Uses recorded in the selected date's period. For by-count benefits with a
// dollar value each record is one use — count records (usage_count may be 0 on
// records logged before the benefit became by-count). Pure count-based
// benefits sum usage_count, using the recalculated count for the current
// period (handles anniversary reset basis + optimistic updates).
const countUsedForSelectedPeriod = computed(() => {
    if (!model.value.date || !props.selectedBenefit) return 0;

    const periodType = props.selectedBenefit.benefit?.period_type;
    const selectedPeriodKey = store.getPeriodKey(model.value.date, periodType);
    const currentPeriodKey = store.getPeriodKey(new Date(), periodType);

    const recordsInPeriod = (props.selectedBenefit.usage_records || []).filter(
        (record) =>
            store.getPeriodKey(record.used_at, periodType) === selectedPeriodKey
    );

    if (isByCountValueBenefit.value) {
        return recordsInPeriod.length;
    }

    if (selectedPeriodKey === currentPeriodKey) {
        return (
            store.recalculateCurrentPeriodUsage(props.selectedBenefit)
                .count_used_current_period || 0
        );
    }
    return recordsInPeriod.reduce(
        (sum, record) => sum + (parseFloat(record.usage_count) || 0),
        0
    );
});

const remainingCount = computed(() => {
    const maxUses = maxCount.value;
    return Math.max(0, maxUses - countUsedForSelectedPeriod.value);
});

// Per-transaction cap for by-count benefits (value_details.count > 1): a
// single entry can't exceed the version's per-use amount even when the
// period still has room (e.g. a 2 × $250 credit caps each entry at $250
// though $500 remains). Mirrors the backend
// ValidateBenefitUsagePerTransactionAmount rule and the desktop
// add-usage-popover.vue pattern (displayAmountForSelectedDate / count).
// Honours a matching monthly override-month value. Infinity (no cap) for
// single-allotment benefits.
const perTransactionMaxAmount = computed(() => {
    const benefit = props.selectedBenefit;
    const effectiveVersion = effectiveVersionForSelectedDate.value;
    const valueDetails =
        effectiveVersion?.value_details ||
        benefit?.benefit?.value_details ||
        {};
    const count = parseFloat(valueDetails.count) || 1;
    if (count <= 1) return Infinity;

    const periodType =
        effectiveVersion?.period_type || benefit?.benefit?.period_type;
    if (periodType?.toLowerCase() === 'monthly') {
        const overrideMonth = valueDetails.override_month?.month;
        const overrideValue = valueDetails.override_month?.value;
        if (overrideMonth && overrideValue != null && model.value.date) {
            const selectedMonth = String(
                dayjs(model.value.date).month() + 1
            ).padStart(2, '0');
            if (overrideMonth === selectedMonth) {
                return parseFloat(overrideValue) || 0;
            }
        }
    }

    return (parseFloat(displayAmountForSelectedDate.value) || 0) / count;
});

const validateForm = () => {
    formErrors.value = {};

    // Check if there's an active version for the selected date
    if (
        props.selectedBenefit?.version_history?.length > 0 &&
        !hasActiveVersionForSelectedDate.value
    ) {
        formErrors.value.usageDate =
            'No benefit version was active on this date';
        return false;
    }

    if (isCountBased.value) {
        if (!model.value.count) {
            formErrors.value.usageCount = 'Enter count';
        }
        if (model.value.count > remainingCount.value) {
            formErrors.value.usageCount = `Max count is ${remainingCount.value}`;
        }
    } else {
        if (!model.value.amount) {
            formErrors.value.usageAmount = 'Enter amount';
        }
        const effectiveMaxAmount = Math.min(
            parseFloat(remainingAmount.value),
            perTransactionMaxAmount.value
        );
        if (model.value.amount > effectiveMaxAmount) {
            formErrors.value.usageAmount = `Max amount is $${effectiveMaxAmount.toFixed(2)}`;
            if (props.selectedBenefit?.benefit?.value_details?.count > 1) {
                formErrors.value.usageAmount += ` per use`;
            }
        }
        // By-count benefits are also capped on the number of uses per period
        // (mirrors backend ValidateBenefitUsageCount). Each amount entry is one
        // use, so block once the period's uses are exhausted.
        if (isByCountValueBenefit.value && remainingCount.value < 1) {
            formErrors.value.usageAmount = 'No remaining uses this period';
        }
    }

    if (!model.value.date) {
        formErrors.value.usageDate = 'Enter date';
    }

    return Object.keys(formErrors.value).length === 0;
};

const isFormValid = computed(() => {
    if (isCountBased.value) {
        return model.value.count && model.value.count > 0 && model.value.date;
    }
    return model.value.amount && model.value.amount > 0 && model.value.date;
});

// Get the version that was effective at the selected date
const effectiveVersionForSelectedDate = computed(() => {
    const benefit = props.selectedBenefit;
    if (!benefit || !model.value.date) return null;

    const versionHistory = benefit.version_history || [];
    if (versionHistory.length === 0) return null;

    const selectedDate = dayjs(model.value.date);

    // Find the version that was effective at the selected date
    const exactMatch = versionHistory.find((version) => {
        const effectiveStart = dayjs(version.effective_start);
        const effectiveEnd = version.effective_end
            ? dayjs(version.effective_end)
            : null;

        // Check if selected date is within this version's effective period
        const afterStart =
            selectedDate.isSame(effectiveStart, 'day') ||
            selectedDate.isAfter(effectiveStart);
        const beforeEnd = !effectiveEnd || selectedDate.isBefore(effectiveEnd);

        return afterStart && beforeEnd;
    });

    if (exactMatch) return exactMatch;

    // If no exact match, check if the date is before the earliest version
    // In this case, use the earliest version (assumes benefit existed before versioning was implemented)
    const sortedVersions = [...versionHistory].sort(
        (a, b) =>
            dayjs(a.effective_start).valueOf() -
            dayjs(b.effective_start).valueOf()
    );
    const earliestVersion = sortedVersions[0];

    if (
        earliestVersion &&
        selectedDate.isBefore(dayjs(earliestVersion.effective_start))
    ) {
        // Return the earliest version for dates before versioning started
        return earliestVersion;
    }

    return null;
});

// Check if the selected date has an active version
const hasActiveVersionForSelectedDate = computed(() => {
    return effectiveVersionForSelectedDate.value !== null;
});

// Display amount for the selected date (handles version history and monthly overrides)
const displayAmountForSelectedDate = computed(() => {
    const benefit = props.selectedBenefit;
    if (!benefit) return 0;

    // First, check version history for the selected date
    const effectiveVersion = effectiveVersionForSelectedDate.value;

    // If there's version history but no version is active for this date, return 0
    if (benefit.version_history?.length > 0 && !effectiveVersion) {
        return 0;
    }

    // Use the effective version's amount if available, otherwise fall back to current benefit amount
    const baseAmount = effectiveVersion
        ? parseFloat(effectiveVersion.amount) || 0
        : parseFloat(benefit.benefit?.amount) || 0;

    const valueDetails =
        effectiveVersion?.value_details || benefit.benefit?.value_details || {};
    const periodType =
        effectiveVersion?.period_type || benefit.benefit?.period_type;

    // SUPPORT-775 follow-up (Devin review): derive by-count semantics from the
    // effective version's `count` when available, falling back to the parent
    // benefit's `values_summary.is_count_based`. The backend version_history
    // projection (CardBenefitsService.php:1499-1508) does not expose
    // `values_summary` per version, so a historical by-count version would
    // otherwise be misread when the current version is no longer by-count.
    // `count > 1` is the reliable version-scoped signal and is also what
    // perTransactionMaxAmount already uses to decide whether a per-use cap
    // exists.
    const versionCount = parseFloat(valueDetails.count) || 1;
    const isByCount =
        versionCount > 1 ||
        benefit.benefit?.values_summary?.is_count_based === true;

    // For monthly benefits, check if selected date's month matches override month
    if (periodType?.toLowerCase() === 'monthly') {
        const overrideMonth = valueDetails.override_month?.month;
        const overrideValue = valueDetails.override_month?.value;

        if (overrideMonth && overrideValue != null && model.value.date) {
            // Use dayjs to avoid timezone issues when extracting month
            const selectedMonth = String(
                dayjs(model.value.date).month() + 1
            ).padStart(2, '0');

            if (overrideMonth === selectedMonth) {
                let periodValue = parseFloat(overrideValue) || 0;
                if (versionCount > 1) {
                    periodValue *= versionCount;
                }
                return periodValue;
            }
        }

        // SUPPORT-775 follow-up: monthly non-override months must also apply
        // the count multiplier for by-count benefits (2 × $50 credit is
        // $100/month, not $50). Mirrors store.calculateMonthlyValue's
        // `hasCountMultiplier` branch. Without this, perTransactionMaxAmount
        // (displayAmount / count) would halve the per-use cap.
        return isByCount && baseAmount > 0
            ? baseAmount * versionCount
            : baseAmount;
    }

    // Non-monthly by-count benefits: the period total is `count × per-use amount`
    // (SUPPORT-775). Inline branch (Option B, justified): the natural fit
    // would be to delegate to store.calculateDisplayAmount, but the store
    // helper reads the current date for monthly + first-year checks; this
    // modal must resolve versions and monthly overrides by the SELECTED
    // date. Inline replication of the store's is_count_based branch is
    // the minimum change that fixes the bug without regressing those
    // selected-date semantics.
    if (isByCount) {
        return baseAmount > 0 ? baseAmount * versionCount : versionCount;
    }

    return baseAmount;
});

// Calculate usage for the selected period
const usageForSelectedPeriod = computed(() => {
    if (!model.value.date || !props.selectedBenefit) return 0;

    // Use period type from effective version if available, otherwise from benefit
    const effectiveVersion = effectiveVersionForSelectedDate.value;
    const periodType =
        effectiveVersion?.period_type ||
        props.selectedBenefit.benefit?.period_type;

    const selectedPeriodKey = store.getPeriodKey(model.value.date, periodType);
    const currentPeriodKey = store.getPeriodKey(new Date(), periodType);

    // If selected period is current period, use current_period_usage
    if (selectedPeriodKey === currentPeriodKey) {
        return props.selectedBenefit.current_period_usage || 0;
    }

    // Otherwise, calculate usage from usage_records for that period
    const usageRecords = props.selectedBenefit.usage_records || [];
    return usageRecords
        .filter((record) => {
            const recordPeriodKey = store.getPeriodKey(
                record.used_at,
                periodType
            );
            return recordPeriodKey === selectedPeriodKey;
        })
        .reduce(
            (sum, record) =>
                sum +
                (isNcred.value
                    ? parseFloat(record.usage_count) || 0
                    : parseFloat(record.total_amount_used) || 0),
            0
        );
});

const remainingAmount = computed(() => {
    const benefitAmount = displayAmountForSelectedDate.value;
    const usedAmount = usageForSelectedPeriod.value;
    return Math.max(0, benefitAmount - usedAmount).toFixed(2);
});

// Largest amount the "Max" pill can fill: the period's remaining value, capped
// by the per-transaction limit for by-count benefits (mirrors the
// effectiveMaxAmount used in validateForm()).
const maxFillableAmount = computed(() =>
    Math.min(parseFloat(remainingAmount.value), perTransactionMaxAmount.value)
);

// Reset form when modal opens
watch(
    () => props.showAddUsageModal,
    (isOpen) => {
        if (isOpen) {
            model.value = {
                amount: null,
                count: null,
                date: dayjs().format('YYYY-MM-DD'),
            };
            formErrors.value = {};
        }
    }
);

const closeModal = () => {
    emit('close');
    // Reset form after a short delay to allow modal to close
    setTimeout(() => {
        model.value = {
            amount: null,
            count: null,
            date: dayjs().format('YYYY-MM-DD'),
        };
        formErrors.value = {};
    }, 300);
};

const handleAdd = () => {
    if (!validateForm()) return;

    emit('add', {
        amount: model.value.amount,
        count: model.value.count,
        date: model.value.date,
    });
    closeModal();
};
</script>
